import React, { useRef, useState } from 'react';
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';
import { soaApi } from '@/services/api';
import { Button } from '@/components/ui';
import { SoaUploadSummary } from '@/types/soa';
import { monthEndDate, previousMonthValue } from './monthEnd';

// One request per 50 files. The whole selection in a single request trips App
// Engine's 32 MB cap or its 10-minute deadline long before the file count matters,
// and a batch that dies mid-ingest leaves the operator guessing what landed.
const UPLOAD_BATCH_SIZE = 50;

const emptySummary = (): SoaUploadSummary => ({
    created: 0,
    updated: 0,
    matched: 0,
    unmatched: 0,
    unmatched_reasons: {},
    errors: [],
});

const mergeSummary = (into: SoaUploadSummary, batch: SoaUploadSummary): SoaUploadSummary => {
    into.created += batch.created;
    into.updated += batch.updated;
    into.matched += batch.matched;
    into.unmatched += batch.unmatched;
    into.errors.push(...batch.errors);
    Object.entries(batch.unmatched_reasons).forEach(([reason, count]) => {
        into.unmatched_reasons[reason] = (into.unmatched_reasons[reason] ?? 0) + count;
    });
    return into;
};

const errorDetail = (err: unknown): string =>
    (axios.isAxiosError(err) && err.response?.data?.detail) || 'upload failed';

interface SoaUploadDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called after a successful upload so the list can reload. */
    onUploaded: (summary: SoaUploadSummary) => void;
}

const SoaUploadDialog: React.FC<SoaUploadDialogProps> = ({ open, onClose, onUploaded }) => {
    const [files, setFiles] = useState<File[]>([]);
    const [month, setMonth] = useState(previousMonthValue);
    const [zipPassword, setZipPassword] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploaded, setUploaded] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const hasZip = files.some(file => file.name.toLowerCase().endsWith('.zip'));

    const reset = () => {
        setFiles([]);
        setMonth(previousMonthValue());
        setZipPassword('');
        setError(null);
        setUploaded(0);
    };

    const acceptFiles = (incoming: FileList | null) => {
        const dropped = Array.from(incoming ?? []);
        if (!dropped.length) return;
        const picked = dropped.filter(file => /\.(pdf|zip)$/i.test(file.name));
        if (!picked.length) {
            setError('Only .pdf and .zip files are supported.');
            return;
        }
        setFiles(picked);
        setError(null);
    };

    const handleClose = () => {
        if (uploading) return;
        reset();
        onClose();
    };

    const handleUpload = async () => {
        if (!files.length || !month) return;
        setUploading(true);
        setError(null);
        setUploaded(0);

        const periodEnd = monthEndDate(month);
        const summary = emptySummary();

        for (let start = 0; start < files.length; start += UPLOAD_BATCH_SIZE) {
            const batch = files.slice(start, start + UPLOAD_BATCH_SIZE);
            try {
                mergeSummary(summary, await soaApi.upload(batch, periodEnd, zipPassword || undefined));
            } catch (err) {
                // One failed batch must not cost the operator the rest of the month.
                // Recorded per file, so the "N failed" count means files, not requests.
                const detail = errorDetail(err);
                batch.forEach(file => summary.errors.push(`${file.name}: ${detail}`));
            }
            setUploaded(start + batch.length);
        }

        setUploading(false);

        // Nothing landed at all — keep the dialog open so the selection can be retried.
        if (!summary.created && !summary.updated) {
            setError(summary.errors[0] || 'Upload failed. Check the files and the zip password, then try again.');
            return;
        }

        // Re-uploading a stored statement replaces it, so retrying the failures is safe.
        onUploaded(summary);
        reset();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload SOA</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Alert severity="info" sx={{ mb: 2 }}>
                    Files are matched to clients automatically; anything unmatched appears
                    in the list for manual assignment.
                </Alert>

                <Box
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={event => {
                        event.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={event => {
                        event.preventDefault();
                        setDragOver(false);
                        acceptFiles(event.dataTransfer.files);
                    }}
                    sx={{
                        mb: 1,
                        p: 3,
                        textAlign: 'center',
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '2px dashed',
                        borderColor: dragOver ? 'primary.main' : 'divider',
                        bgcolor: dragOver ? 'action.hover' : 'transparent',
                        transition: 'border-color 0.15s, background-color 0.15s',
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: 32, color: dragOver ? 'primary.main' : 'text.secondary', mb: 0.5 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {files.length === 0
                            ? 'Drag & drop PDFs or a ZIP here, or click to browse'
                            : `${files.length} file${files.length === 1 ? '' : 's'} selected — click to replace`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Any number of SOA PDFs, a zip of them, or a mix
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.zip"
                        multiple
                        hidden
                        onChange={event => {
                            acceptFiles(event.target.files);
                            // Lets the same selection be re-picked after a failure.
                            event.target.value = '';
                        }}
                    />
                </Box>

                {files.length > 0 && (
                    <List dense sx={{ mb: 1, maxHeight: 180, overflowY: 'auto' }}>
                        {files.map((file, index) => (
                            <ListItem
                                key={`${file.name}-${index}`}
                                disableGutters
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        disabled={uploading}
                                        onClick={() => setFiles(files.filter((_, i) => i !== index))}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                }
                            >
                                <ListItemText
                                    primary={file.name}
                                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

                <TextField
                    label="Statement Month"
                    type="month"
                    value={month}
                    onChange={event => setMonth(event.target.value)}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                />

                {hasZip && (
                    <TextField
                        label="ZIP Password (only if the archive itself is encrypted)"
                        type="password"
                        value={zipPassword}
                        onChange={event => setZipPassword(event.target.value)}
                        fullWidth
                        helperText="Not the PDF password — those are derived from each client's PAN."
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
                <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={!files.length || !month || uploading}
                >
                    {uploading ? `Uploading ${uploaded}/${files.length}…` : 'Upload'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SoaUploadDialog;
